import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { iCell, iSquare } from '../../interfaces/sudoku';

interface SudokuProps {
  width: number;
  height: number;
  cell: iCell[];
}

interface matrixProps {
  x: number;
  y: number;
  newValue: string;
}

interface ContextProps {
  score: number;
  isSudokuFinished: boolean;
  height: number;
  width: number;
  solution: iCell[];
  showAnswers: boolean;
  matrix: iSquare[][];
  numberOfHints: number;
  setValuesSquares: ({ newValue, x, y }: matrixProps) => void;
  getStart: ({ width, height }: SudokuProps) => void;
  setScore: (score: number) => void;
  setMatrix: (initialMatrix: iSquare[][]) => void;
  handleCheckResults: () => number;
  cleanGame: () => void;
  handleShowAnswers: () => void;
  handleShowOneAnswer: () => void;
  setSolution: (solution: iCell[]) => void;
  checkFocus: (x: number, y: number) => void;
  setIsSudokuFinished: (isFinished: boolean) => void;
  verifyFinish: () => void;
}

const SudokuContext = createContext({} as ContextProps);

const SudokuProvider: React.FC = ({ children }) => {
  const [score, setScore] = useState<number>(0); //Pontuação

  const [solution, setSolution] = useState<iCell[]>([]);

  const [numberOfHints, setNumberOfHints] = useState<number>(0); //Número de dicas

  const [width, setWidth] = useState<number>(0); //largura
  const [height, setHeight] = useState<number>(0); //altura

  // Última posição que foi focada.
  const [positionLastFocusX, setPositionLastFocusX] = useState(-1);
  const [positionLastFocusY, setPositionLastFocusY] = useState(-1);

  //Estado: Já resolvido ou não
  const [isSudokuFinished, setIsSudokuFinished] = useState<boolean>(false);

  //Estado: Mostrar todas as respostas ou não
  const [showAnswers, setShowAnswers] = useState<boolean>(false);

  //Estado: Limpar ou não
  // const [clear, setClear] = useState<boolean>(false);

  useEffect(() => {
    let hintsCount = 0;
    solution.forEach((element) => {
      if (element.attributes.hint) {
        hintsCount++;
      }
    });

    setNumberOfHints(hintsCount);
  }, [solution]);

  // Estado que guarda a Matriz gerada pelo Sudoku
  const [matrix, setMatrix] = useState<iSquare[][]>([]);

  /* Começa o sudoku, criando uma matrix vazia */
  const getStart = useCallback(({ width, height, cell }: SudokuProps) => {
    setHeight(height);
    setWidth(width);
    setScore(0);
    setShowAnswers(false);

    let newMatrix: iSquare[][] = [];
    for (let i = 0; i < width; i++) {
      newMatrix[i] = [];
      for (let j = 0; j < height; j++) {
        if (cell[i * 9 + j]?.attributes.hint) {
          newMatrix[i][j] = {
            value: cell[i * 9 + j]?.attributes.solution,
          };
        } else {
          newMatrix[i][j] = {
            value: '',
          };
        }
      }
    }

    setMatrix(newMatrix);
  }, []);

  //Função: Limpar todos os dados do jogo
  const cleanGame = useCallback(() => {
    setScore(0);
    setShowAnswers(false);
    if (isSudokuFinished) {
      setIsSudokuFinished(false);
    }
    getStart({ width, height, cell: solution });
  }, [getStart, height, isSudokuFinished, solution, width]); //Função: Limpar os inputs do jogo

  //Função: Mostrar todas as respostas
  const handleShowAnswers = useCallback(() => {
    setShowAnswers(true);
    setIsSudokuFinished(true);
  }, []);

  /* Seta os valores do input dentro do Estado de Matriz */
  const setValuesSquares = useCallback(
    ({ x, y, newValue }: matrixProps) => {
      let newMatrix = JSON.parse(JSON.stringify(matrix));

      newMatrix[x][y].value = newValue;
      setMatrix(newMatrix);
    },
    [matrix]
  );

  // Função: Mostrar uma resposta
  const handleShowOneAnswer = useCallback(() => {
    const newValue =
      solution[positionLastFocusX * 9 + positionLastFocusY].attributes.solution;

    // fazer tratativas para caso ele clique 2x no mesmo quadro ja respondido

    const newSolutions = solution;

    newSolutions[positionLastFocusX * 9 + positionLastFocusY].attributes.hint =
      'true';
    setSolution(newSolutions);

    setValuesSquares({
      x: positionLastFocusX,
      y: positionLastFocusY,
      newValue,
    });
  }, [positionLastFocusX, positionLastFocusY, setValuesSquares, solution]);

  //Função: Conferir o jogo
  const handleCheckResults = useCallback(() => {
    setIsSudokuFinished(true);
    let checkScore = 0;

    for (let i = 0; i < width; i++) {
      for (let j = 0; j < height; j++) {
        if (matrix && matrix[0][0]) {
          if (
            matrix[i][j]?.value === solution[i * 9 + j]?.attributes.solution &&
            !solution[i * 9 + j]?.attributes.hint
          ) {
            checkScore += 2;
          }
        }
      }
    }

    setScore(checkScore);
    return checkScore;
  }, [height, matrix, solution, width]);

  const verifyFinish = useCallback(() => {
    let num = 0;

    for (let i = 0; i < width; i++) {
      if (num > 0) {
        break;
      }
      for (let j = 0; j < height; j++) {
        if (matrix && matrix[i][j].value === '') {
          num++;
          break;
        }
      }
    }

    if (num === 0) {
      setIsSudokuFinished(true);
    }
  }, [height, matrix, width]);

  /* Verifica a última casa focada. */
  const checkFocus = useCallback((x: number, y: number) => {
    setPositionLastFocusX(x);
    setPositionLastFocusY(y);
  }, []);

  const value = useMemo(
    () => ({
      score,
      isSudokuFinished,
      height,
      width,
      solution,
      showAnswers,
      matrix,
      setScore,
      setSolution,
      setIsSudokuFinished,
      numberOfHints,
      setMatrix,
    }),
    [
      height,
      isSudokuFinished,
      matrix,
      numberOfHints,
      score,
      showAnswers,
      solution,
      width,
    ]
  );

  return (
    <SudokuContext.Provider
      value={{
        ...value,
        handleCheckResults,
        cleanGame,
        handleShowAnswers,
        handleShowOneAnswer,
        checkFocus,
        verifyFinish,
        setValuesSquares,
        getStart,
      }}
    >
      {children}
    </SudokuContext.Provider>
  );
};

function useSudoku(): ContextProps {
  const context = useContext(SudokuContext);

  if (!context) {
    throw new Error('useTimer must be used within an TimerProvider');
  }

  return context;
}

export { SudokuProvider, useSudoku };
